/**
 * Bulk operations — batch status changes & deletions.
 */

import { ObjectStatus, ObjectType } from "./db-types.js"
import type { DbObject, DbStartAtIndex } from "./db-types.js"
import { getDb, callUndo, getNow, emitEvent } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { toMsTimestamp } from "./utils.js"
import { reopenContainerIfClosed } from "./containerStatusOps.js"
import { runCompletionHooksFor } from "./taskCompletionHooks.js"
import {
  isDescendantOf,
  loadDeletedSince,
  loadDescendants,
  restoreCascade,
  uniqueDeletedAt,
} from "./helpers/trashHelpers.js"

// ---------------------------------------------------------------------------
// Targets: which of the given ids a bulk operation acts on, and why it skips the rest
// ---------------------------------------------------------------------------

/** An id a bulk operation did not act on, and why. */
export interface BulkSkip {
  id: string
  reason: string
}

/** What a bulk operation did: `count` objects changed (not ids.length), `skipped` the rest. */
export interface BulkResult {
  count: number
  skipped: BulkSkip[]
}

/**
 * Why bulkComplete / bulkCancel skip a project or list: closing one may leave open tasks inside a
 * closed container (they vanish from Today and Upcoming), so it goes through closeContainer, which
 * asks what happens to them.
 */
const CLOSE_VIA_CONTAINER = "a project or list is completed or canceled with closeContainer (it decides what happens to its open tasks)"

/** The task-status bulk ops (complete / cancel) leave projects and lists to closeContainer. */
const notContainer = (row: DbObject): string | undefined =>
  row.type === ObjectType.Project || row.type === ObjectType.List ? CLOSE_VIA_CONTAINER : undefined

/** Reopen the closed containers that open tasks among `rows` are about to be put into. */
async function reopenContainersOf(rows: readonly DbObject[]): Promise<void> {
  const paths = new Set<string>()
  for (const t of rows) if (t.type === ObjectType.Task) paths.add(t.parentPath)
  for (const path of paths) await reopenContainerIfClosed(path)
}

export interface BulkOptions {
  /**
   * Act on live tasks only: skip objects in the trash and anything that is not a task (a project,
   * a section, ...). The app's own bulk edits run on whatever the user selected, containers
   * included, so this is off by default; the MCP tools turn it on.
   */
  liveTasksOnly?: boolean
}

const noop = (): BulkResult => ({ count: 0, skipped: [] })

/**
 * Split `ids` into the rows to change and the ids to report as skipped. Each id counts once.
 * `unchanged` names the reason a row needs no change (already completed, ...), or undefined.
 */
function resolveTargets(
  ids: readonly string[],
  rows: ReadonlyArray<DbObject | undefined>,
  options: BulkOptions | undefined,
  unchanged?: (row: DbObject) => string | undefined,
): { targets: DbObject[]; skipped: BulkSkip[] } {
  const targets: DbObject[] = []
  const skipped: BulkSkip[] = []
  const seen = new Set<string>()
  ids.forEach((id, i) => {
    if (seen.has(id)) return
    seen.add(id)
    const row = rows[i]
    if (!row) return void skipped.push({ id, reason: "not found" })
    if (options?.liveTasksOnly) {
      if (row.isDeleted === 1) return void skipped.push({ id, reason: "deleted" })
      if (row.type !== ObjectType.Task && row.type !== ObjectType.Subtask) {
        return void skipped.push({ id, reason: "not a task" })
      }
    }
    const reason = unchanged?.(row)
    if (reason) return void skipped.push({ id, reason })
    targets.push(row)
  })
  return { targets, skipped }
}

// ---------------------------------------------------------------------------
// bulkComplete
// ---------------------------------------------------------------------------

export async function bulkComplete(
  ids: string[],
  options?: BulkOptions,
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    const { targets: toUpdate, skipped } = resolveTargets(
      ids,
      await db.totodo.bulkGet(ids),
      options,
      (t) => notContainer(t) ?? (t.status === ObjectStatus.Completed ? "already completed" : undefined),
    )

    if (toUpdate.length === 0) return ok({ count: 0, skipped })

    const SECTION_OFFSET_MS = 1000
    const updates = toUpdate.map((t) => {
      const isSection = t.type === ObjectType.Section
      const effectiveTimestamp = isSection
        ? String(Number(timestamp) + SECTION_OFFSET_MS)
        : timestamp
      return {
        key: t.id as string,
        changes: {
          status: ObjectStatus.Completed,
          statusAt: effectiveTimestamp,
          updatedAt: timestamp,
        },
      }
    })

    await db.totodo.bulkUpdate(updates)

    // The same hooks as completeTask: a running timer is stopped (it would run on where nobody can
    // stop it: useActiveTimers skips completed tasks), a recurrence instance records its outcome and
    // generates the next one, a routine step unlocks the next
    await runCompletionHooksFor(toUpdate, "completed")

    const prevStatuses = toUpdate.map((t) => ({
      id: t.id as string,
      status: t.status,
      statusAt: t.statusAt,
    }))
    callUndo(
      `Completed ${toUpdate.length} tasks`,
      async () => {
        const db = getDb()
        const ts = getNow()
        await db.totodo.bulkUpdate(
          prevStatuses.map((p) => ({
            key: p.id,
            changes: { status: p.status, statusAt: p.statusAt, updatedAt: ts },
          })),
        )
      },
      async () => {
        await bulkComplete(ids, options)
      },
      "batch",
    )

    emitEvent("bulk_completed", { count: toUpdate.length })
    return ok({ count: toUpdate.length, skipped })
  } catch (e) {
    return fail(
      `bulkComplete failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkDelete (soft)
// ---------------------------------------------------------------------------

/**
 * Soft-delete the tasks themselves. Their descendants stay as they are; on restore they come
 * back as restoreCascade decides. Each task gets a deletedAt that no checklist row deleted on its
 * own under it carries, so a restore cannot mistake such a row for one that went with the task.
 * Only rows deleted from `timestamp` on can carry such a stamp: one read finds them for all the
 * tasks (loadDeletedSince), so a large selection is not one descendant read per task.
 */
async function trashTasks(
  tasks: readonly DbObject[],
  timestamp: string,
): Promise<void> {
  const db = getDb()
  await db.transaction("rw", [db.totodo], async () => {
    const recent = await loadDeletedSince(timestamp)
    const updates = tasks.map((t) => ({
      key: t.id as string,
      changes: {
        isDeleted: 1 as const,
        deletedAt: uniqueDeletedAt(timestamp, recent.filter((d) => isDescendantOf(d, t))),
        updatedAt: timestamp,
      },
    }))
    await db.totodo.bulkUpdate(updates)
  })
}

export async function bulkDelete(
  ids: string[],
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    const { targets: toUpdate, skipped } = resolveTargets(
      ids,
      await db.totodo.bulkGet(ids),
      undefined,
      (t) => (t.isDeleted === 1 ? "already in the trash" : undefined),
    )

    if (toUpdate.length === 0) return ok({ count: 0, skipped })

    await trashTasks(toUpdate, timestamp)

    const trashedIds = toUpdate.map((t) => t.id as string)
    callUndo(
      `Deleted ${toUpdate.length} tasks`,
      async () => {
        const db = getDb()
        const ts = getNow()
        await db.totodo.bulkUpdate(
          trashedIds.map((tId) => ({
            key: tId,
            changes: { isDeleted: 0 as const, updatedAt: ts },
          })),
        )
      },
      async () => {
        await bulkDelete(ids)
      },
      "batch",
    )

    emitEvent("bulk_deleted", { count: toUpdate.length })
    return ok({ count: toUpdate.length, skipped })
  } catch (e) {
    return fail(
      `bulkDelete failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkUncomplete
// ---------------------------------------------------------------------------

export async function bulkUncomplete(
  ids: string[],
  options?: BulkOptions & {
    /**
     * Also reopen canceled tasks (what uncompleteTask does for one). Off by default: "uncomplete"
     * means completed -> open, and the app only ever passes completed ids.
     */
    includeCanceled?: boolean
  },
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    const { targets: toUpdate, skipped } = resolveTargets(
      ids,
      await db.totodo.bulkGet(ids),
      options,
      (t) => {
        if (t.status === ObjectStatus.Unfinished) return "already open"
        if (t.status === ObjectStatus.Canceled && !options?.includeCanceled) {
          return "canceled, not completed (pass includeCanceled to reopen it)"
        }
        return undefined
      },
    )

    if (toUpdate.length === 0) return ok({ count: 0, skipped })

    // Open tasks in a closed project / list reopen it (first: see reopenContainerIfClosed)
    await reopenContainersOf(toUpdate)

    const updates = toUpdate.map((t) => ({
      key: t.id as string,
      changes: {
        status: ObjectStatus.Unfinished,
        statusAt: timestamp,
        updatedAt: timestamp,
      },
    }))

    await db.totodo.bulkUpdate(updates)

    const prevStatuses = toUpdate.map((t) => ({
      id: t.id as string,
      status: t.status,
      statusAt: t.statusAt,
    }))
    callUndo(
      `Uncompleted ${toUpdate.length} tasks`,
      async () => {
        const db = getDb()
        const ts = getNow()
        await db.totodo.bulkUpdate(
          prevStatuses.map((p) => ({
            key: p.id,
            changes: { status: p.status, statusAt: p.statusAt, updatedAt: ts },
          })),
        )
      },
      async () => {
        await bulkUncomplete(ids, options)
      },
      "batch",
    )

    return ok({ count: toUpdate.length, skipped })
  } catch (e) {
    return fail(
      `bulkUncomplete failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkSchedule
// ---------------------------------------------------------------------------

export async function bulkSchedule(
  ids: string[],
  startAt: string | undefined,
  type?: number,
  subType?: number,
  options?: BulkOptions,
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    let count = 0

    // An id with no object behind it gets no schedule entry (it would be an orphan row); with
    // liveTasksOnly the trash and non-tasks are left alone too.
    const { targets, skipped } = resolveTargets(ids, await db.totodo.bulkGet(ids), options)
    if (targets.length === 0) return ok({ count: 0, skipped })
    const requested = ids
    ids = targets.map((t) => t.id as string)

    const lastEntry = await db.startAtIndex.orderBy("index").last()
    let nextIdx = lastEntry ? lastEntry.index + 1 : 0

    // Capture previous states for undo
    const prevEntries: Array<{
      id: string
      entry: DbStartAtIndex | undefined
    }> = []
    for (const id of ids) {
      const entryId = `#${id}`
      const existing = await db.startAtIndex.get(entryId)
      prevEntries.push({ id, entry: existing ? { ...existing } : undefined })
    }

    // Normalize startAt to ms-timestamp if provided
    const normalizedStartAt =
      startAt != null ? toMsTimestamp(startAt) ?? startAt : undefined

    await db.transaction("rw", [db.totodo, db.startAtIndex], async () => {
      for (const id of ids) {
        const entryId = `#${id}`
        const existing = await db.startAtIndex.get(entryId)

        if (normalizedStartAt === undefined) {
          // No start date — update type/subType on existing entry, or create without startAt
          if (existing) {
            await db.startAtIndex.update(entryId, {
              type: type ?? existing.type,
              subType: subType ?? existing.subType,
            })
          } else {
            await db.startAtIndex.add({
              id: entryId,
              index: nextIdx++,
              startAt: "",
              type: type ?? 1,
              subType: subType ?? 1,
            })
          }
        } else if (existing) {
          await db.startAtIndex.update(entryId, {
            startAt: normalizedStartAt,
            type: type ?? existing.type,
            subType: subType ?? existing.subType,
          })
        } else {
          await db.startAtIndex.add({
            id: entryId,
            index: nextIdx++,
            startAt: normalizedStartAt,
            type: type ?? 1,
            subType: subType ?? 1,
          })
        }

        await db.totodo.update(id, { updatedAt: timestamp })
        count++
      }
    })

    callUndo(
      `Scheduled ${count} tasks`,
      async () => {
        const db = getDb()
        for (const prev of prevEntries) {
          const entryId = `#${prev.id}`
          if (prev.entry) {
            await db.startAtIndex.put(prev.entry)
          } else {
            try {
              await db.startAtIndex.delete(entryId)
            } catch {
              /* may not exist */
            }
          }
        }
      },
      async () => {
        await bulkSchedule(requested, startAt, type, subType, options)
      },
      "batch",
    )

    return ok({ count, skipped })
  } catch (e) {
    return fail(
      `bulkSchedule failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkCancel
// ---------------------------------------------------------------------------

export async function bulkCancel(
  ids: string[],
  options?: BulkOptions,
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    const { targets: toUpdate, skipped } = resolveTargets(
      ids,
      await db.totodo.bulkGet(ids),
      options,
      (t) => notContainer(t) ?? (t.status === ObjectStatus.Canceled ? "already canceled" : undefined),
    )

    if (toUpdate.length === 0) return ok({ count: 0, skipped })

    const updates = toUpdate.map((t) => ({
      key: t.id as string,
      changes: {
        status: ObjectStatus.Canceled,
        statusAt: timestamp,
        updatedAt: timestamp,
      },
    }))

    await db.totodo.bulkUpdate(updates)

    // As cancelTask: a running timer is stopped
    await runCompletionHooksFor(toUpdate, "canceled")

    const prevStatuses = toUpdate.map((t) => ({
      id: t.id as string,
      status: t.status,
      statusAt: t.statusAt,
    }))
    callUndo(
      `Canceled ${toUpdate.length} tasks`,
      async () => {
        const db = getDb()
        const ts = getNow()
        await db.totodo.bulkUpdate(
          prevStatuses.map((p) => ({
            key: p.id,
            changes: { status: p.status, statusAt: p.statusAt, updatedAt: ts },
          })),
        )
      },
      async () => {
        await bulkCancel(ids, options)
      },
      "batch",
    )

    emitEvent("bulk_canceled", { count: toUpdate.length })
    return ok({ count: toUpdate.length, skipped })
  } catch (e) {
    return fail(
      `bulkCancel failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkRestore (from soft-delete)
// ---------------------------------------------------------------------------

export async function bulkRestore(
  ids: string[],
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    const { targets: toUpdate, skipped } = resolveTargets(
      ids,
      await db.totodo.bulkGet(ids),
      undefined,
      (t) => (t.isDeleted === 1 ? undefined : "not in the trash"),
    )

    if (toUpdate.length === 0) return ok({ count: 0, skipped })

    // Open tasks restored into a closed project / list reopen it (first: see reopenContainerIfClosed)
    await reopenContainersOf(toUpdate.filter((t) => t.status === ObjectStatus.Unfinished))

    // Restore each task + what went to the trash with it — like restoreFromTrash, not a
    // checklist row the user had deleted on its own (see trashHelpers).
    // Read before writing: dexie-sqlite does not roll back a failed transaction.
    await db.transaction("rw", [db.totodo], async () => {
      for (const t of toUpdate) {
        const restored = restoreCascade(t, await loadDescendants(t))
        await db.totodo.update(t.id as string, {
          isDeleted: 0,
          updatedAt: timestamp,
        })
        await db.totodo.bulkUpdate(
          restored.map((d) => ({
            key: d.id as string,
            changes: { isDeleted: 0 as const, updatedAt: timestamp },
          })),
        )
      }
    })

    const restoredIds = toUpdate.map((t) => t.id as string)
    callUndo(
      `Restored ${toUpdate.length} tasks`,
      async () => {
        const tasks = await getDb().totodo.bulkGet(restoredIds)
        await trashTasks(
          tasks.filter((t): t is NonNullable<typeof t> => t != null),
          getNow(),
        )
      },
      async () => {
        await bulkRestore(ids)
      },
      "batch",
    )

    emitEvent("bulk_restored", { count: toUpdate.length })
    return ok({ count: toUpdate.length, skipped })
  } catch (e) {
    return fail(
      `bulkRestore failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// bulkUpdateDueDate
// ---------------------------------------------------------------------------

export async function bulkUpdateDueDate(
  ids: string[],
  dueAt: string | undefined,
  options?: BulkOptions,
): Promise<OpResult<BulkResult>> {
  const db = getDb()
  if (ids.length === 0) return ok(noop())

  try {
    const timestamp = getNow()
    const { targets: toUpdate, skipped } = resolveTargets(
      ids,
      await db.totodo.bulkGet(ids),
      options,
    )

    if (toUpdate.length === 0) return ok({ count: 0, skipped })

    const normalizedDueAt = toMsTimestamp(dueAt) ?? dueAt

    await db.totodo.bulkUpdate(
      toUpdate.map((t) => ({
        key: t.id as string,
        changes: {
          dueAt: normalizedDueAt,
          updatedAt: timestamp,
        },
      })),
    )

    const prevDueDates = toUpdate.map((t) => ({
      id: t.id as string,
      dueAt: t.dueAt,
    }))
    callUndo(
      dueAt
        ? `Set deadline on ${toUpdate.length} tasks`
        : `Cleared deadline on ${toUpdate.length} tasks`,
      async () => {
        const db = getDb()
        const ts = getNow()
        await db.totodo.bulkUpdate(
          prevDueDates.map((p) => ({
            key: p.id,
            changes: { dueAt: p.dueAt, updatedAt: ts },
          })),
        )
      },
      async () => {
        await bulkUpdateDueDate(ids, dueAt, options)
      },
      "batch",
    )

    emitEvent("bulk_due_date_updated", { count: toUpdate.length })
    return ok({ count: toUpdate.length, skipped })
  } catch (e) {
    return fail(
      `bulkUpdateDueDate failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}
